<template>
  <div class="padding-top padding-right">
    <c-page-section v-if="hasManagePermission() || hasReadPermission()" squares class="q-mb-lg">
      <q-toolbar
        v-if="hasManagePermission() || hasReadPermission()"
        ref="pageToolbarRef"
        class="page-toolbar"
        :class="{ column: isMobile }"
      >
        <div
          :class="{
            'col-xs-3 col-md-3 filter-dropdowns-wrapper': !isMobile,
            'full-width filter-dropdowns-wrapper': isMobile,
          }"
        >
          <div class="page-toolbar-dropdown">
            <user-dropdown
              v-model:selectedItemValue="filter.collaboratorUuid"
              data-testid="user-filter-dropdown"
              :items="collaboratorsDropdown"
              :label="t('collabNotes.collaborator')"
            />
          </div>
        </div>
        <q-separator vertical />
        <q-space />
        <div
          :class="{
            'col-xs-3 col-md-2': !isMobile,
            'full-width': isMobile,
          }"
        >
          <div>
            <div class="page-toolbar-action">
              <toolbar-add-button
                v-if="hasManagePermission()"
                data-testid="add-note"
                :label="t('collabNotes.addNote')"
                :disabled="authStore.currentUser?.uuid === filter.collaboratorUuid || !filter.collaboratorUuid"
                @click="openAddDialog"
              />
            </div>
          </div>
        </div>
      </q-toolbar>
    </c-page-section>

    <div>
      <collaborator-note-record
        v-for="item of collabNotesModule.collabNotes"
        :key="item.uuid"
        :data="item"
        class="q-pb-md"
        :allow-delete="hasManagePermission() && (canManageAll || item.editor.uuid === authStore.currentUser?.uuid)"
        :allow-edit="hasManagePermission() && (canManageAll || item.editor.uuid === authStore.currentUser?.uuid)"
        @click:delete="onDeleteNoteClick"
        @click:edit="onEditNoteClick"
      />
    </div>
    <c-alert
      class="q-mt-lg"
      type="info"
      translate-message="common.noTableData"
      :show="collabNotesModule.collabNotes.length <= 0"
      hide-close-button
    />
    <collaborator-note-dialog ref="noteDialog" @saved="onSaved" />
    <c-confirm-dialog ref="confirmDialog" />
  </div>
</template>

<script lang="ts" setup>
import CPageSection from '@/components/_common/PageSection.vue';
import { useI18n } from 'vue-i18n';
import { computed, ref, watch } from 'vue';
import { UserDropdownItem } from '@/components/_common/dropdown/DropdownItem';
import userModule from '@/store/modules/UserModule';
import collabNotesModule from '@/store/modules/CollaboratorNotesModule';
import { useCollabNote } from '@/composables/useCollabNote';
import { hubClient } from '@tfpl/api-client';
import CAlert from '@/components/_common/CommonAlert.vue';
import CConfirmDialog from '@/components/_common/confirmDialog/ConfirmDialog.vue';
import UserDropdown from '@/components/_common/dropdown/UserDropdown.vue';
import ToolbarAddButton from '@/components/_common/ToolbarAddButton.vue';
import { useWindowDimensions } from '@/composables/useWindowDimensions';
import { bp720 } from '@/constants/breakpoints';
import { useAuthStore } from '@/store/auth';
import { useAuthorization } from '@/composables/auth/useAuthorization';
import { permissions } from '@/store/modules/PositionModule';
import CollaboratorNoteDialog from '@/components/collaboratorNotes/CollaboratorNoteDialog.vue';
import CollaboratorNoteRecord from '@/components/collaboratorNotes/CollaboratorNoteRecord.vue';

const { t } = useI18n();
const authStore = useAuthStore();
const { hasLoggedUserPermission } = useAuthorization();

const windowDimensions = useWindowDimensions();
const isMobile = computed(() => windowDimensions.width < bp720);

const canManageAll = hasLoggedUserPermission(permissions.HUB_COLLABORATOR_NOTES.MANAGE_ALL);
const canManageTeamMembers = hasLoggedUserPermission(permissions.HUB_COLLABORATOR_NOTES.MANAGE_TEAM_MEMBER);
const canReadAll = hasLoggedUserPermission(permissions.HUB_COLLABORATOR_NOTES.READ_ALL);
const canReadTeamMembers = hasLoggedUserPermission(permissions.HUB_COLLABORATOR_NOTES.READ_TEAM_MEMBER);

/**
 * Permissions
 */
const hasManagePermission = () => {
  return canManageAll || canManageTeamMembers;
};
const hasReadPermission = () => {
  return canReadAll || canReadTeamMembers;
};

/**
 * Filter
 */
const filter = ref<{
  collaboratorUuid?: string;
}>({
  collaboratorUuid: undefined,
});

/**
 * Collaborators
 */
if (hasManagePermission() || hasReadPermission()) {
  userModule.fetchUsers({});
}
const collaboratorsDropdown = computed((): UserDropdownItem[] => {
  if (!hasManagePermission() && !hasReadPermission()) {
    return [];
  }

  let filteredUsers = userModule.users.filter((u) => u.isActive && u.uuid !== authStore.currentUser?.uuid);

  if (!canManageAll && !canReadAll) {
    filteredUsers = filteredUsers.filter(
      (u) => u.responsiblePerson && u.responsiblePerson.uuid === authStore.currentUser?.uuid,
    );
  }

  return filteredUsers.map((u) => ({
    name: u.fullName,
    value: u.uuid,
    avatarUrl: u.slackAvatarUrl,
  }));
});

/**
 * User notes
 */
watch(
  filter,
  (newFilter) => {
    if ((hasManagePermission() || hasReadPermission()) && newFilter.collaboratorUuid) {
      collabNotesModule.fetchCollabNotes({
        collaboratorUuid: newFilter.collaboratorUuid,
      });
    } else {
      collabNotesModule.clearData();
    }
  },
  { deep: true, immediate: true },
);

/**
 * Add / edit dialog
 */
const noteDialog = ref();

const openAddDialog = () => {
  if (filter.value.collaboratorUuid) {
    noteDialog.value.openDialog(filter.value.collaboratorUuid);
  }
};

const onSaved = () => {
  if (filter.value.collaboratorUuid) {
    collabNotesModule.fetchCollabNotes({
      collaboratorUuid: filter.value.collaboratorUuid,
    });
  }
};

const onEditNoteClick = (note: hubClient.CollaboratorNoteDto) => {
  if (filter.value.collaboratorUuid) {
    noteDialog.value.openDialog(filter.value.collaboratorUuid, note);
  }
};

/**
 * Delete dialog
 */
const confirmDialog = ref();
const { deleteNote } = useCollabNote();
const onDeleteNoteClick = (uuid: string) => {
  confirmDialog.value.showDialog({
    title: t('collabNotes.deleteConfirm'),
    cancelBtnLabel: t('cancelBtn'),
    confirmBtnLabel: t('delete'),
    confirmAction: async () => {
      await deleteNote(uuid);
      await collabNotesModule.fetchCollabNotes({
        collaboratorUuid: filter.value.collaboratorUuid as string,
      });
      confirmDialog.value.closeDialog();
    },
  });
};
</script>
<style lang="scss" scoped>
@import 'styles/app.variables.scss';
.page-toolbar-action {
  width: auto;
}
.page-toolbar-dropdown {
  max-width: none;
}
</style>
